/**
 * river_geometry — the PURE geometry the river renderer and its harnesses
 * share (2026-09-11, "rivers are still floating above the terrain … the
 * water fall needs a particle effect … the water is going up the waterfall").
 *
 * THE RIBBON FOLLOWS THE LEDGE. The carve holds a waterfall reach's water at
 * the upstream level to the lip (t = 0.5) and drops it over FALL_LIP_RUN
 * (shared/terrain.ts riverCarveSeg); the ribbon used to draw a straight strip
 * from point to point, so it ran UNDER the held ground before the lip and IN
 * THE AIR after it — the floating water in the user's screenshots. The rows
 * here break the strip at the lip and the foot; the sheet covers the drop.
 *
 * THE SHEET IS A NAPPE. Water leaving a lip is a projectile: s = v0·t,
 * y = −½·g·t² (the free-fall parabola of a plunge fall; a slack river still
 * arcs at NAPPE_V0_MIN). The sheet is that curve, subdivided, landing inside
 * the carve's run so it meets the ledge's foot.
 */
import { FALL_LIP_RUN } from '../../../shared/terrain.js';
import { RIVER_STRIDE, RIVER_FLAG_FALL } from './hydrology_bake.js';

export const G_FALL = 9.81;
/** Rows down the free-fall curve (the sheet's vertical detail). */
export const NAPPE_ROWS = 14;
/** Vertices across the sheet. */
export const NAPPE_COLS = 6;
/** The water leaves the lip at least this fast (m/s). */
export const NAPPE_V0_MIN = 1.6;
/** The sheet dips this far under the pool surface so its seam hides in the foam. */
export const NAPPE_DIP = 0.35;

/** The carve's water surface along a reach (mirrors riverCarveSeg). */
export function reachWater(wa: number, wb: number, len: number, fall: boolean, t: number): number {
  if (!fall) return wa + (wb - wa) * t;
  const run = len > 0 ? FALL_LIP_RUN / len : 1;
  const u = t <= 0.5 ? 0 : t >= 0.5 + run ? 1 : (t - 0.5) / run;
  return wa + (wb - wa) * (u * u * (3 - 2 * u));
}

/** Where the ribbon breaks around a fall: the lip and the foot, as reach parameters. */
export function fallBreak(len: number): { lipT: number; footT: number } {
  return { lipT: 0.5, footT: Math.min(1, 0.5 + (len > 0 ? FALL_LIP_RUN / len : 1)) };
}

export interface RibbonRow {
  x: number; z: number;
  /** The water surface (no lift applied). */
  y: number;
  /** Half-width incl. the lip. */
  hw: number;
  /** Unit downstream tangent. */
  tx: number; tz: number;
  /** Bed depth mid-stream. */
  depth: number;
  /** false = the strip is NOT joined to the previous row (a fall's drop — the sheet covers it). */
  joined: boolean;
  /** Reach parameter along the segment starting at point k (0 at a point). */
  t: number;
}

/**
 * The ribbon's rows over the consecutive points run[] of river r: one row per
 * point and, for a fall reach, the LIP row (held level) and the FOOT row
 * (downstream level, FALL_LIP_RUN on) with the strip cut between them.
 */
export function ribbonRows(r: number[], run: number[], lip: number): RibbonRow[] {
  const rows: RibbonRow[] = [];
  const at = (k: number, c: number): number => r[k * RIVER_STRIDE + c];
  for (let i = 0; i < run.length; i++) {
    const k = run[i];
    const kp = run[Math.max(0, i - 1)], kn = run[Math.min(run.length - 1, i + 1)];
    let tx = at(kn, 0) - at(kp, 0), tz = at(kn, 1) - at(kp, 1);
    const tl = Math.hypot(tx, tz) || 1;
    tx /= tl; tz /= tl;
    rows.push({ x: at(k, 0), z: at(k, 1), y: at(k, 2), hw: at(k, 3) * 0.5 + lip, tx, tz, depth: at(k, 4), joined: true, t: 0 });
    if (i < run.length - 1 && (at(k, 5) & RIVER_FLAG_FALL) !== 0) {
      const b = run[i + 1];
      let dx = at(b, 0) - at(k, 0), dz = at(b, 1) - at(k, 1);
      const len = Math.hypot(dx, dz) || 1;
      dx /= len; dz /= len;
      const { lipT, footT } = fallBreak(len);
      const hwAt = (t: number): number => (at(k, 3) + (at(b, 3) - at(k, 3)) * t) * 0.5 + lip;
      const depthAt = (t: number): number => at(k, 4) + (at(b, 4) - at(k, 4)) * t;
      rows.push({ x: at(k, 0) + dx * len * lipT, z: at(k, 1) + dz * len * lipT, y: at(k, 2), hw: hwAt(lipT), tx: dx, tz: dz, depth: depthAt(lipT), joined: true, t: lipT });
      rows.push({ x: at(k, 0) + dx * len * footT, z: at(k, 1) + dz * len * footT, y: at(b, 2), hw: hwAt(footT), tx: dx, tz: dz, depth: depthAt(footT), joined: false, t: footT });
    }
  }
  return rows;
}

export interface NappePoint {
  /** Horizontal metres from the lip along the flow. */
  s: number;
  /** Metres below the lip (≤ 0). */
  y: number;
  /** Arc length from the lip (m) — the sheet's v coordinate. */
  v: number;
}

/**
 * The free-fall curve from the lip to the pool `drop` metres below: the water
 * leaves at v0 (≥ NAPPE_V0_MIN), throttled so it lands inside FALL_LIP_RUN
 * (where the carve's ledge foot is). `rows` samples from the lip (row 0) to the
 * pool surface.
 */
export function nappeCurve(drop: number, v0 = NAPPE_V0_MIN, rows = NAPPE_ROWS): { pts: NappePoint[]; v0: number; landing: number; tEnd: number } {
  const d = Math.max(0.1, drop);
  const tEnd = Math.sqrt((2 * d) / G_FALL);
  const vUse = Math.min(Math.max(v0, NAPPE_V0_MIN), (FALL_LIP_RUN * 0.85) / tEnd);
  const pts: NappePoint[] = [];
  let v = 0;
  for (let i = 0; i <= rows; i++) {
    const t = tEnd * (i / rows);
    const s = vUse * t, y = -0.5 * G_FALL * t * t;
    if (i > 0) v += Math.hypot(s - pts[i - 1].s, y - pts[i - 1].y);
    pts.push({ s, y, v });
  }
  return { pts, v0: vUse, landing: pts[rows].s, tEnd };
}

export interface FallFxRecipe {
  sprayRate: number; sprayMax: number;
  mistRate: number; mistMax: number; mistSize: number;
  crestRate: number; crestMax: number;
}

/**
 * Particle budgets for one fall from its width and drop, scaled by the tier's
 * particleCap (0 = none — the weather's law). Rates × lifetimes stay under the
 * pools (spray ≤ 1.1 s, mist ≤ 4.5 s, crest ≤ 0.9 s).
 */
export function fallFxRecipe(width: number, drop: number, cap: number): FallFxRecipe | null {
  if (cap <= 0) return null;
  const w = Math.min(12, Math.max(1.5, width)), dr = Math.min(30, Math.max(3, drop));
  const sprayRate = Math.round((120 + 30 * w + 8 * dr) * cap);
  const mistRate = Math.round((12 + 2.5 * w + 1.2 * dr) * cap);
  const crestRate = Math.round((24 + 5 * w) * cap);
  return {
    sprayRate, sprayMax: Math.ceil(sprayRate * 1.15) + 32,
    mistRate, mistMax: Math.ceil(mistRate * 4.6) + 16, mistSize: 1.2 + Math.min(2.4, dr * 0.09),
    crestRate, crestMax: Math.ceil(crestRate * 0.95) + 16,
  };
}
